export function DigitalPulse() {
  // ViewBox uses 1920x1080 user-space units. Path data must be absolute numbers
  // (CSS units like vw/vh are not valid inside SVG path d / animateMotion path).
  // Container scales the SVG to fit; preserveAspectRatio="xMidYMid slice" keeps it filling.
  const path1 = "M -100 100 C 200 150, 300 300, 960 540";
  const path2 = "M -100 864 C 200 756, 400 648, 960 540";
  const path3 = "M 2304 100 C 1536 200, 1344 300, 960 540";
  const path4 = "M 2304 972 C 1536 864, 1152 648, 960 540";
  const path5 = "M 960 -100 C 960 200, 960 300, 960 540";
  const path6 = "M 960 1180 C 960 800, 960 700, 960 540";

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-50 mix-blend-screen">
      <svg
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="hub-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="12" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Faint paths representing cables */}
        <g stroke="rgba(59, 130, 246, 0.15)" strokeWidth="1.5" fill="none">
          <path id="path1" d={path1} />
          <path id="path2" d={path2} />
          <path id="path3" d={path3} />
          <path id="path4" d={path4} />
          <path id="path5" d={path5} />
          <path id="path6" d={path6} />
        </g>

        {/* Central Hub Ripple Effects */}
        <g filter="url(#hub-glow)">
          <circle cx="960" cy="540" r="10" fill="none" stroke="#38BDF8" strokeWidth="2">
            <animate attributeName="r" values="10;80" dur="3s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.8;0" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="960" cy="540" r="10" fill="none" stroke="#60A5FA" strokeWidth="2">
            <animate attributeName="r" values="10;120" dur="3s" begin="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0" dur="3s" begin="1.5s" repeatCount="indefinite" />
          </circle>
          {/* Static glowing core */}
          <circle cx="960" cy="540" r="6" fill="#FFFFFF" opacity="0.9">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Moving data packets */}
        <g filter="url(#glow)">
          <circle r="4" fill="#60A5FA" opacity="0.9">
            <animateMotion dur="4s" repeatCount="indefinite" path={path1} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="4s" repeatCount="indefinite" />
          </circle>

          <circle r="3" fill="#3B82F6" opacity="0.9">
            <animateMotion dur="5s" begin="1.5s" repeatCount="indefinite" path={path2} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="5s" begin="1.5s" repeatCount="indefinite" />
          </circle>

          <circle r="4" fill="#38BDF8" opacity="0.9">
            <animateMotion dur="4.5s" begin="0.5s" repeatCount="indefinite" path={path3} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="4.5s" begin="0.5s" repeatCount="indefinite" />
          </circle>

          <circle r="3.5" fill="#818CF8" opacity="0.9">
            <animateMotion dur="6s" begin="2s" repeatCount="indefinite" path={path4} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="6s" begin="2s" repeatCount="indefinite" />
          </circle>

          <circle r="3" fill="#93C5FD" opacity="0.9">
            <animateMotion dur="3.5s" begin="1s" repeatCount="indefinite" path={path5} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="3.5s" begin="1s" repeatCount="indefinite" />
          </circle>

          <circle r="3.5" fill="#38BDF8" opacity="0.9">
            <animateMotion dur="4.2s" begin="0.8s" repeatCount="indefinite" path={path6} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="4.2s" begin="0.8s" repeatCount="indefinite" />
          </circle>

          {/* Secondary pulses on existing paths for density */}
          <circle r="2.5" fill="#E0F2FE" opacity="0.8">
            <animateMotion dur="4s" begin="2s" repeatCount="indefinite" path={path1} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="4s" begin="2s" repeatCount="indefinite" />
          </circle>

          <circle r="2.5" fill="#BAE6FD" opacity="0.8">
            <animateMotion dur="4.5s" begin="2.5s" repeatCount="indefinite" path={path3} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.8;1" dur="4.5s" begin="2.5s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Random blinking network nodes */}
        <g fill="#38BDF8">
          <circle cx="200" cy="150" r="1.5">
            <animate attributeName="opacity" values="0;0.8;0" dur="3s" repeatCount="indefinite" />
          </circle>
          <circle cx="300" cy="300" r="2">
            <animate attributeName="opacity" values="0;1;0" dur="4s" begin="1s" repeatCount="indefinite" />
          </circle>
          <circle cx="1536" cy="200" r="1.5">
            <animate attributeName="opacity" values="0;0.7;0" dur="2.5s" begin="0.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="1152" cy="648" r="2.5">
            <animate attributeName="opacity" values="0;0.9;0" dur="5s" begin="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="400" cy="648" r="1.5">
            <animate attributeName="opacity" values="0;0.6;0" dur="3.5s" begin="1.2s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>
    </div>
  );
}
